/**
 * Display names for catalog objects
 *
 * Objects are stored under SIMBAD's main identifier, which is sometimes a star
 * ("HD 50896" for Sh2-308), a cluster label ("Cl Melotte 22" for M 45) or a
 * "NAME ..." entry, and their aliases include padded and survey identifiers
 * ("NGC0302", "SDSS J005625.30-103947.6"). Users know objects by catalog
 * designation, so the display name is, in order:
 *   1. a Messier designation among the aliases (NGC 224 -> M 31)
 *   2. the object's own catalog designation (catalog + number)
 *   3. the cleaned primary name
 *
 * @package Astrophotography Planning Calculator
 */

const CATALOG_DESIGNATIONS = {
    Messier: (n) => `M ${n}`,
    NGC: (n) => `NGC ${n}`,
    IC: (n) => `IC ${n}`,
    Caldwell: (n) => `Caldwell ${n}`,
    Sharpless: (n) => `Sh2-${n}`,
    Barnard: (n) => `B ${n}`,
    LBN: (n) => `LBN ${n}`,
    LDN: (n) => `LDN ${n}`,
    vdB: (n) => `vdB ${n}`,
    RCW: (n) => `RCW ${n}`,
    Gum: (n) => `Gum ${n}`,
    Abell: (n) => `Abell ${n}`,
    Collinder: (n) => `Cr ${n}`,
    'Cometary Globule': (n) => `CG ${n}`,
    'Wolf-Rayet': (n) => `WR ${n}`,
    BAT99: (n) => `BAT99 ${n}`,
    'SMC AB': (n) => `SMC AB ${n}`
};

// "M 31" / "M31", but not Minkowski planetary nebulae such as "M 1-41"
const MESSIER_ALIAS = /^M\s*(\d{1,3})$/;

// Short object_type codes some catalog builders stored, spelled out
const TYPE_CODES = {
    DSO: 'Deep-Sky Object',
    DN: 'Dark Nebula',
    BN: 'Bright Nebula',
    RN: 'Reflection Nebula',
    PN: 'Planetary Nebula',
    HII: 'HII Region',
    CGb: 'Cometary Globule',
    WR: 'Wolf-Rayet Star'
};

/**
 * Readable object type, e.g. {object_type: 'DN'} -> "Dark Nebula"
 * @param {{object_type?: string, type?: string}} object - Catalog object (bookmarks store "type")
 * @returns {string} Empty when the type isn't known
 */
export function objectTypeLabel(object) {
    const type = String(object?.object_type || object?.type || '').trim();
    return TYPE_CODES[type] || type;
}

/**
 * Tidy a stored name: drop SIMBAD's "NAME " prefix and repeated spaces
 * @param {string} name
 * @returns {string}
 */
export function cleanObjectName(name) {
    return String(name || '').replace(/^NAME\s+/, '').replace(/\s{2,}/g, ' ').trim();
}

/**
 * The object's designation in its own catalog, e.g. {catalog: 'Sharpless', catalog_number: '308'} -> "Sh2-308"
 * @param {{catalog?: string, catalog_number?: string|number}} object
 * @returns {string|null}
 */
export function catalogDesignation(object) {
    const format = object && CATALOG_DESIGNATIONS[object.catalog];
    const number = String(object?.catalog_number ?? '').trim();
    return format && /^\d+[A-Za-z]?$/.test(number) ? format(number) : null;
}

/**
 * Choose the name to show for an object
 * @param {{name?: string, catalog?: string, catalog_number?: string|number}} object
 * @param {string[]} [aliasNames] - The object's aliases
 * @returns {string}
 */
export function chooseDisplayName(object, aliasNames = []) {
    if (object?.catalog !== 'Messier') {
        for (const alias of aliasNames) {
            const match = MESSIER_ALIAS.exec(String(alias).trim());
            if (match) return `M ${parseInt(match[1], 10)}`;
        }
    }
    return catalogDesignation(object) || cleanObjectName(object?.name) || 'Unknown';
}
